import { useState } from "react";
import { useParams } from "react-router-dom";
import { Card, Table, Tag, Button, Modal, Form, Input, DatePicker, InputNumber, Select, message, Space, Statistic, Row, Col } from "antd";
import { useBillingInvoices, useBillingInvoiceCreate, useBillingInvoiceStatus } from "../../../api-hooks/billing";
import { usePlanList } from "../../../api-hooks/plans";
import dayjs from "dayjs";

const STATUS_COLOR: Record<string, string> = {
  draft: "default", sent: "blue", paid: "green", failed: "red", refunded: "orange",
};

export default function BillingPage() {
  const { companyId } = useParams<{ companyId: string }>();
  const cid = Number(companyId);
  const [form] = Form.useForm();
  const [createOpen, setCreateOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [messageApi, ctx] = message.useMessage();

  const { data: resp, isLoading } = useBillingInvoices(cid, { page, limit: 999, status: statusFilter });
  const rows = resp?.data ?? [];
  const total = resp?.total ?? 0;
  const { data: plans = [] } = usePlanList();

  const create = useBillingInvoiceCreate({ onSuccess: () => { messageApi.success("Invoice created"); setCreateOpen(false); }, onError: () => messageApi.error("Failed") });
  const updateStatus = useBillingInvoiceStatus({ onSuccess: () => messageApi.success("Status updated"), onError: () => messageApi.error("Failed") });

  const totalPaid = rows.filter((r: any) => r.status === "paid").reduce((s: number, r: any) => s + Number(r.total), 0);
  const totalOutstanding = rows.filter((r: any) => ["draft","sent"].includes(r.status)).reduce((s: number, r: any) => s + Number(r.total), 0);

  const columns = [
    { title: "Invoice #", dataIndex: "invoice_number", render: (v: string) => <span className="font-mono text-xs">{v}</span> },
    { title: "Plan", dataIndex: "plan_name" },
    { title: "Amount", render: (_: any, r: any) => `£${Number(r.total).toFixed(2)}` },
    { title: "Period", render: (_: any, r: any) => `${dayjs(r.billing_period_start).format("DD MMM YYYY")} – ${dayjs(r.billing_period_end).format("DD MMM YYYY")}` },
    { title: "Status", dataIndex: "status", render: (v: string) => <Tag color={STATUS_COLOR[v] || "default"}>{v}</Tag> },
    { title: "Paid At", dataIndex: "paid_at", render: (v: string) => v ? dayjs(v).format("DD MMM YYYY") : "—" },
    {
      title: "Actions", key: "actions", render: (_: any, row: any) => (
        <Space size="small">
          {row.status === "draft" && <Button size="small" onClick={() => updateStatus.mutate({ cid, id: row.id, status: "sent" })}>Mark Sent</Button>}
          {row.status === "sent" && <Button size="small" type="primary" onClick={() => updateStatus.mutate({ cid, id: row.id, status: "paid" })}>Mark Paid</Button>}
          {row.status === "paid" && <Button size="small" onClick={() => updateStatus.mutate({ cid, id: row.id, status: "refunded" })}>Refund</Button>}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {ctx}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">Platform Billing</h2>
          <p className="text-slate-500 text-sm mt-0.5">Company #{cid} — subscription invoices</p>
        </div>
        <Button type="primary" onClick={() => { form.resetFields(); setCreateOpen(true); }}>Create Invoice</Button>
      </div>

      <Row gutter={16} className="mb-4">
        <Col span={8}>
          <Card className="rounded-2xl border border-slate-200 shadow-sm">
            <Statistic title="Total Paid" value={totalPaid} prefix="£" precision={2} valueStyle={{ color: "#16a34a" }} />
          </Card>
        </Col>
        <Col span={8}>
          <Card className="rounded-2xl border border-slate-200 shadow-sm">
            <Statistic title="Outstanding" value={totalOutstanding} prefix="£" precision={2} valueStyle={{ color: "#2563eb" }} />
          </Card>
        </Col>
        <Col span={8}>
          <Card className="rounded-2xl border border-slate-200 shadow-sm">
            <Statistic title="Total Invoices" value={total} />
          </Card>
        </Col>
      </Row>

      <Card className="rounded-2xl border border-slate-200 shadow-sm">
        <div className="flex gap-3 mb-4">
          <Select allowClear placeholder="Filter status" value={statusFilter} onChange={setStatusFilter} style={{ width: 140 }}
            options={["draft","sent","paid","failed","refunded"].map(s => ({ value: s, label: s }))} />
        </div>
        <Table dataSource={rows} columns={columns} rowKey="id" loading={isLoading} size="small"
          pagination={rows.length > 50 ? { pageSize: 50, showSizeChanger: false } : false} />
      </Card>

      <Modal title="Create Invoice" open={createOpen} onCancel={() => setCreateOpen(false)}
        onOk={() => form.validateFields().then(v => create.mutate({ cid, data: { ...v, billing_period_start: v.billing_period_start.format("YYYY-MM-DD"), billing_period_end: v.billing_period_end.format("YYYY-MM-DD") } }))}
        okText="Create" confirmLoading={create.isPending} width={560}>
        <Form form={form} layout="vertical" className="mt-4">
          <div className="grid grid-cols-2 gap-3">
            <Form.Item name="plan_id" label="Plan" rules={[{ required: true }]} className="col-span-2">
              <Select options={plans.map((p: any) => ({ value: p.id, label: p.name }))} />
            </Form.Item>
            <Form.Item name="invoice_number" label="Invoice #" rules={[{ required: true }]}>
              <Input placeholder="INV-2025-001" />
            </Form.Item>
            <Form.Item name="currency" label="Currency" initialValue="GBP">
              <Select options={[{ value: "GBP", label: "GBP" }, { value: "USD", label: "USD" }]} />
            </Form.Item>
            <Form.Item name="amount" label="Amount (ex VAT)" rules={[{ required: true }]}>
              <InputNumber prefix="£" min={0} precision={2} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="vat_amount" label="VAT" initialValue={0}>
              <InputNumber prefix="£" min={0} precision={2} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="total" label="Total" rules={[{ required: true }]}>
              <InputNumber prefix="£" min={0} precision={2} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="billing_period_start" label="Period Start" rules={[{ required: true }]}>
              <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
            </Form.Item>
            <Form.Item name="billing_period_end" label="Period End" rules={[{ required: true }]}>
              <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
